import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { resolveMirror } from '../../services/mirrorApi';
import './QRLogin.css';
import BrandLogo from '../../components/BrandLogo';

const isMongoObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || '').trim());
const suggestBranchSuffix3 = (value) => {
  const v = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return (v.slice(0, 3) || 'BRN').padEnd(3, 'X').slice(0, 3);
};
const suggestBrandCode2 = (name) => {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const a = (words[0]?.[0] || '').toUpperCase();
  const b = (words[1]?.[0] || words[0]?.[1] || '').toUpperCase();
  const raw = `${a}${b}`.replace(/[^A-Z0-9]/g, '');
  return (raw || 'BC').padEnd(2, 'X').slice(0, 2);
};

const QRLogin = () => {
  const [qrValue, setQrValue] = useState('');
  // Mirror Code shown/encoded in QR (e.g., BC-HSR-001)
  const [mirrorId, setMirrorId] = useState('');
  const [salonId, setSalonId] = useState(''); // Branch (Salon) ObjectId
  const [inputMirrorId, setInputMirrorId] = useState('');
  const [inputSalonId, setInputSalonId] = useState(''); // Branch ID input
  const [isVerified, setIsVerified] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [salonName, setSalonName] = useState('');
  const [salonAddress, setSalonAddress] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = (params.get('mirrorId') || localStorage.getItem('mirrorCode') || localStorage.getItem('mirrorId') || '').trim();
    const sId = (params.get('salonId') || localStorage.getItem('salonId') || '').trim();
    if (id) setInputMirrorId(id);
    if (sId) setInputSalonId(sId);
  }, []);

  useEffect(() => {
    if (!isVerified || !mirrorId || !salonId) return undefined;
    const baseUrl = window.location.origin;
    const buildMobileUrl = () =>
      `${baseUrl}/mobile-login?mirrorId=${encodeURIComponent(mirrorId)}&salonId=${encodeURIComponent(salonId)}`;

    setQrValue(buildMobileUrl());
    const interval = setInterval(() => setQrValue(buildMobileUrl()), 30000);
    return () => clearInterval(interval);
  }, [isVerified, mirrorId, salonId]);

  const handleValidateMapping = async (e) => {
    e.preventDefault();
    const sanitizedMirrorId = inputMirrorId.trim();
    const sanitizedSalonId = inputSalonId.trim();

    if (!sanitizedMirrorId || !sanitizedSalonId) {
      setValidationError('Mirror ID and Branch ID are required.');
      return;
    }

    setIsValidating(true);
    setValidationError('');
    try {
      const res = await resolveMirror(sanitizedMirrorId);
      const resolvedMirror = res.data?.mirror;
      const resolvedSalonId = resolvedMirror?.salon?._id;
      const resolvedSalonBranchCode = String(resolvedMirror?.salon?.branchCode || '').trim().toUpperCase();
      const brandCode =
        String(resolvedMirror?.salon?.brand?.brandCode || '').trim().toUpperCase() ||
        suggestBrandCode2(resolvedMirror?.salon?.brand?.name);
      const suffix = suggestBranchSuffix3(resolvedMirror?.salon?.branch || resolvedMirror?.salon?.name);
      const fallbackBranchCode = brandCode ? `${brandCode}-${suffix}` : '';
      const resolvedSalonName = resolvedMirror?.salon?.name || '';
      const resolvedSalonAddress = [
        resolvedMirror?.salon?.address?.street,
        resolvedMirror?.salon?.address?.city,
        resolvedMirror?.salon?.address?.state,
        resolvedMirror?.salon?.address?.zipCode,
        resolvedMirror?.salon?.address?.country
      ].filter(Boolean).join(', ');

      if (!resolvedMirror?._id || !resolvedSalonId) {
        throw new Error('Mirror details are incomplete. Contact support.');
      }
      const inputBranch = String(sanitizedSalonId || '').trim();
      const inputBranchUpper = inputBranch.toUpperCase();
      const idMatches = String(resolvedSalonId) === inputBranch;
      const branchCodeMatches =
        Boolean(inputBranchUpper) &&
        (inputBranchUpper === resolvedSalonBranchCode || inputBranchUpper === fallbackBranchCode);

      if (!(idMatches || (!isMongoObjectId(inputBranch) && branchCodeMatches))) {
        throw new Error('Mirror ID is not mapped to this Branch ID.');
      }

      // QR should use mirrorCode, but store internal mirrorId for API/session.
      const mirrorCode = resolvedMirror?.mirrorCode || sanitizedMirrorId;
      const internalMirrorId = resolvedMirror?.mirrorId || resolvedMirror?._id || null;

      setMirrorId(mirrorCode);
      setSalonId(sanitizedSalonId);
      setSalonName(resolvedSalonName);
      setSalonAddress(resolvedSalonAddress);
      setIsVerified(true);
      if (mirrorCode) localStorage.setItem('mirrorCode', mirrorCode);
      if (internalMirrorId) localStorage.setItem('mirrorId', internalMirrorId);
      localStorage.setItem('mirrorObjectId', resolvedMirror._id);
      localStorage.setItem('salonId', sanitizedSalonId);
    } catch (error) {
      setIsVerified(false);
      setValidationError(error.response?.data?.message || error.message || 'Failed to validate mirror mapping.');
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="qr-login-container">
      <div className="qr-login-content">
        {/* Header with icons */}
        <div className="qr-header">
          <BrandLogo variant="compact" className="qr-login-logo" />
          <h1 className="qr-title">Scan to Unlock Your Experience</h1>
        </div>

        <p className="qr-subtitle">No typing needed scan the QR with your phone to continue</p>

        {!isVerified && (
          <form className="qr-mapping-form" onSubmit={handleValidateMapping}>
            <input
              className="qr-mapping-input"
              type="text"
              placeholder="Enter Mirror ID"
              value={inputMirrorId}
              onChange={(e) => setInputMirrorId(e.target.value)}
              disabled={isValidating}
            />
            <input
              className="qr-mapping-input"
              type="text"
              placeholder="Enter Branch ID"
              value={inputSalonId}
              onChange={(e) => setInputSalonId(e.target.value)}
              disabled={isValidating}
            />
            <button className="qr-mapping-button" type="submit" disabled={isValidating}>
              {isValidating ? 'Validating...' : 'Validate and Enable QR'}
            </button>
            {validationError && <p className="qr-mapping-error">{validationError}</p>}
          </form>
        )}

        {isVerified && (
          <div className="qr-code-wrapper">
            <div className="qr-code-container">
              <div className="qr-code-inner">
                <QRCodeSVG
                  value={qrValue}
                  size={250}
                  level="H"
                  includeMargin={false}
                />
              </div>
            </div>
            <p className="qr-security-note">This QR is secure and changes every few seconds.</p>
            <div className="qr-config-details">
              <p className="qr-security-note qr-config-note"><strong>Mirror ID:</strong> {mirrorId}</p>
              <p className="qr-security-note qr-config-note"><strong>Branch ID:</strong> {salonId}</p>
              <p className="qr-security-note qr-config-note"><strong>Branch Name:</strong> {salonName || 'N/A'}</p>
              <p className="qr-security-note qr-config-note"><strong>Address:</strong> {salonAddress || 'N/A'}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRLogin;
